var __defProp = Object.defineProperty;
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var _queue, _scheduled, _initialized;
const requestIdleCallback = typeof window.requestIdleCallback === "function" ? (cb, opts) => window.requestIdleCallback(cb, opts) : (cb) => window.setTimeout(cb, 0);
const yieldToMainThread = () => {
  if ("yield" in scheduler) {
    return scheduler.yield();
  }
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      setTimeout(resolve, 0);
    });
  });
};
function isLowPowerDevice() {
  return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
}
function supportsViewTransitions() {
  return typeof document.startViewTransition === "function";
}
function isMetaInAppBrowser(userAgent = navigator.userAgent) {
  const ua = userAgent || "";
      const androidWebView = /\bAndroid\b/i.test(ua) && /;\s?wv\)/i.test(ua);
      const knownInAppBrowser = /\b(FBAN|FBAV|FB_IAB|FBIOS|Instagram|musical_ly|Bytedance|BytedanceWebview|trill|TikTok)(?:\b|_)/i.test(ua);
      return androidWebView || knownInAppBrowser;
}
const viewTransition = {
  current: void 0
};
const viewTransitionTypes = {
  "product-grid": async () => {
    const grid = document.querySelector(".product-grid");
    const productCards = (
      /** @type {HTMLElement[]} */
      [
        ...document.querySelectorAll(".product-grid .product-grid__item")
      ]
    );
    if (!grid || !productCards.length) return;
    const cleanup = () => productCards.forEach((card) => {
      card.style.removeProperty("view-transition-name");
      card.style.removeProperty("content-visibility");
    });
    try {
      await new Promise((resolve, reject) => requestIdleCallback(() => {
        try {
          const cardsToAnimate = getCardsToAnimate(grid, productCards);
          productCards.forEach((card, index) => {
            if (index < cardsToAnimate) {
              card.style.setProperty("view-transition-name", "product-card-".concat(card.dataset.productId));
            } else {
              card.style.setProperty("content-visibility", "hidden");
            }
          });
          resolve(null);
        } catch (error) {
          reject(error);
        }
      }));
    } catch (error) {
      cleanup();
      throw error;
    }
    return cleanup;
  }
};
async function startViewTransition(callback, types) {
  if (!supportsViewTransitions() || isLowPowerDevice() || prefersReducedMotion() || isMetaInAppBrowser()) {
    return callback();
  }
  const cleanupFunctions = [];
  let updatePromise;
  let completion;
  const update = () => {
    if (!updatePromise) updatePromise = Promise.resolve().then(callback);
    return updatePromise;
  };
  try {
    try {
      if (types) {
        for (const type of types) {
          if (viewTransitionTypes[type]) {
            const cleanupFunction = await viewTransitionTypes[type]();
            if (cleanupFunction) cleanupFunctions.push(cleanupFunction);
          }
        }
      }
    } catch (error) {
      return await update();
    }
    let transition;
    try {
      transition = document.startViewTransition(update);
    } catch (error) {
      return await update();
    }
    // A skipped animation can reject ready even when the DOM update succeeds.
    // Keep callback failures observable through finished/updatePromise below.
    transition.ready.catch(() => {});
    completion = transition.finished.then(() => {
    }, () => {
    });
    viewTransition.current = completion;
    try {
      if (types && transition.types && typeof transition.types.add === "function") {
        types.forEach((type) => transition.types.add(type));
      }
    } catch (error) {
      transition.skipTransition();
    }
    await transition.finished;
    await updatePromise;
  } finally {
    if (completion && viewTransition.current === completion) viewTransition.current = void 0;
    cleanupFunctions.forEach((cleanupFunction) => cleanupFunction());
  }
}
function fetchConfig(type = "json", config = {}) {
  const headers = { "Content-Type": "application/json", Accept: "application/".concat(type), ...config.headers };
  if (type === "javascript") {
    headers["X-Requested-With"] = "XMLHttpRequest";
    delete headers["Content-Type"];
  }
  return {
    method: "POST",
    headers: (
      /** @type {HeadersInit} */
      headers
    ),
    body: config.body
  };
}
function fetchCartJson(url, config, controller, timeout = 3e4) {
  return fetchWithDeadline(url, config, controller, timeout, async (response) => {
    const data = await response.json();
    if (!response.ok) {
      const error = new Error(data && (data.description || data.message || data.errors) || "Cart request failed");
      error.status = response.status;
      error.data = data;
      throw error;
    }
    return data;
  });
}
function fetchWithDeadline(url, config, controller, timeout, readResponse) {
  return new Promise((resolve, reject) => {
    const signal = controller.signal;
    let timer;
    let settled = false;
    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal.removeEventListener("abort", onAbort);
      callback(value);
    };
    const onAbort = () => {
      const error = new Error("Cart request cancelled");
      error.name = "AbortError";
      finish(reject, error);
    };
    if (signal.aborted) {
      onAbort();
      return;
    }
    signal.addEventListener("abort", onAbort);
    timer = setTimeout(() => {
      const error = new Error("Cart update could not be confirmed");
      error.name = "TimeoutError";
      finish(reject, error);
      controller.abort();
    }, timeout);
    Promise.resolve().then(() => {
      if (signal.aborted) {
        onAbort();
        return;
      }
      return fetch(url, Object.assign({}, config, { signal })).then(readResponse);
    }).then((data) => finish(resolve, data), (error) => finish(reject, error));
  });
}
const cartCheckoutOwners = /* @__PURE__ */ new Map();
let cartCheckoutObserver;
let cartCheckoutListening = false;
function guardCartCheckout(event) {
  if (!cartCheckoutOwners.size) return;
  const target = event.target;
  const isCartSubmit = event.type === "submit" && target && target.id === "cart-form";
  const isCheckoutClick = event.type === "click" && target instanceof Element && target.closest("[data-cart-checkout-controls]");
  if (!isCartSubmit && !isCheckoutClick) return;
  event.preventDefault();
  event.stopImmediatePropagation();
}
function refreshCartCheckout() {
  const blocked = cartCheckoutOwners.size > 0;
  const pending = Array.from(cartCheckoutOwners.values()).includes("pending");
  const failed = Array.from(cartCheckoutOwners.values()).includes("failed");
  for (const controls of document.querySelectorAll("[data-cart-checkout-controls]")) {
    controls.disabled = blocked;
    controls.setAttribute("aria-busy", pending ? "true" : "false");
  }
  for (const notice of document.querySelectorAll("[data-cart-checkout-pending]")) notice.hidden = !pending;
  for (const notice of document.querySelectorAll("[data-cart-checkout-review]")) notice.hidden = !failed;
}
function setCartCheckout(owner, state) {
  if (state) cartCheckoutOwners.set(owner, state);
  else cartCheckoutOwners.delete(owner);
  const blocked = cartCheckoutOwners.size > 0;
  if (blocked && !cartCheckoutListening) {
    document.addEventListener("submit", guardCartCheckout, true);
    document.addEventListener("click", guardCartCheckout, true);
    cartCheckoutListening = true;
    if (typeof MutationObserver !== "undefined" && document.documentElement) {
      cartCheckoutObserver = new MutationObserver(refreshCartCheckout);
      cartCheckoutObserver.observe(document.documentElement, { childList: true, subtree: true });
    }
  } else if (!blocked && cartCheckoutListening) {
    document.removeEventListener("submit", guardCartCheckout, true);
    document.removeEventListener("click", guardCartCheckout, true);
    cartCheckoutListening = false;
    if (cartCheckoutObserver) cartCheckoutObserver.disconnect();
    cartCheckoutObserver = null;
  }
  refreshCartCheckout();
}
function debounce(fn, wait) {
  let timeout;
  function debounced(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  }
  debounced.cancel = () => {
    clearTimeout(timeout);
  };
  return (
    /** @type {T & { cancel(): void }} */
    debounced
  );
}
function throttle(fn, delay) {
  let lastCall = 0;
  function throttled(...args) {
    const now = performance.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      fn.apply(this, args);
    }
  }
  throttled.cancel = () => {
    lastCall = performance.now();
  };
  return (
    /** @type {T & { cancel(): void }} */
    throttled
  );
}
const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");
function prefersReducedMotion() {
  return reducedMotion.matches;
}
function normalizeString(str) {
  return str.normalize("NFD").replace(new RegExp("\\p{Diacritic}", "gu"), "").toLowerCase();
}
function onDocumentLoaded(callback) {
  if (document.readyState === "complete") {
    callback();
  } else {
    window.addEventListener("load", callback);
  }
}
function onDocumentReady(callback, options) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", callback, options);
  } else {
    callback();
  }
}
function removeWillChangeOnAnimationEnd(event) {
  const target = event.target;
  if (target && target instanceof HTMLElement) {
    target.style.setProperty("will-change", "unset");
    target.removeEventListener("animationend", removeWillChangeOnAnimationEnd);
  }
}
function onAnimationEnd(elements, callback, options = { subtree: true }) {
  const targets = Array.isArray(elements) ? elements : [elements];
  const animations = targets.reduce((all, element) => {
    return typeof element.getAnimations === "function" ? all.concat(element.getAnimations(options)) : all;
  }, []);
  const animationPromises = animations.reduce(
    (acc, animation) => {
      const isDocumentAnimation = typeof DocumentTimeline !== "undefined"
        ? animation.timeline instanceof DocumentTimeline
        : typeof document !== "undefined" && document.timeline && animation.timeline === document.timeline;
      if (isDocumentAnimation) {
        acc.push(animation.finished);
      }
      return acc;
    },
    /** @type {Promise<Animation>[]} */
    []
  );
  const settled = typeof Promise.allSettled === "function"
    ? Promise.allSettled(animationPromises)
    : Promise.all(animationPromises.map((promise) => Promise.resolve(promise).then(
      (value) => ({ status: "fulfilled", value }),
      (reason) => ({ status: "rejected", reason })
    )));
  return settled.then(callback);
}
const scrollLockOwners = /* @__PURE__ */ new Set();
let scrollLockObserver;
function pruneDisconnectedScrollLockOwners() {
  for (const owner of scrollLockOwners) {
    if (!owner.isConnected) {
      scrollLockOwners.delete(owner);
    }
  }
}
function syncScrollLock() {
  pruneDisconnectedScrollLockOwners();
  if (scrollLockOwners.size > 0) {
    document.documentElement.setAttribute("scroll-lock", "");
    if (!scrollLockObserver) {
      scrollLockObserver = new MutationObserver(syncScrollLock);
      scrollLockObserver.observe(document.documentElement, { childList: true, subtree: true });
    }
    return;
  }
  document.documentElement.removeAttribute("scroll-lock");
  scrollLockObserver == null ? void 0 : scrollLockObserver.disconnect();
  scrollLockObserver = void 0;
}
function lockScroll(owner) {
  scrollLockOwners.add(owner);
  syncScrollLock();
}
function unlockScroll(owner) {
  scrollLockOwners.delete(owner);
  syncScrollLock();
}
function isClickedOutside(event, element) {
  if (event.target instanceof HTMLDialogElement || !(event.target instanceof Element)) {
    return !isPointWithinElement(event.clientX, event.clientY, element);
  }
  return !element.contains(event.target);
}
function isPointWithinElement(x, y, element) {
  const { left, right, top, bottom } = element.getBoundingClientRect();
  return x >= left && x <= right && y >= top && y <= bottom;
}
const mediaQueryLarge = matchMedia("(min-width: 750px)");
function isMobileBreakpoint() {
  return !mediaQueryLarge.matches;
}
function isDesktopBreakpoint() {
  return mediaQueryLarge.matches;
}
function isTouchDevice() {
  return "ontouchstart" in window && navigator.maxTouchPoints > 0;
}
function clamp(value, min, max) {
  return Math.max(min, Math.min(value, max));
}
function center(element, axis) {
  const { left, width, top, height } = element.getBoundingClientRect();
  const point = {
    x: left + width / 2,
    y: top + height / 2
  };
  if (axis) return (
    /**  @type {any} */
    point[axis]
  );
  return (
    /**  @type {any} */
    point
  );
}
function start(element, axis) {
  const { left, top } = element.getBoundingClientRect();
  const point = { x: left, y: top };
  if (axis) return (
    /**  @type {any} */
    point[axis]
  );
  return (
    /**  @type {any} */
    point
  );
}
function closest(values, target) {
  return values.reduce(function(prev, curr) {
    return Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev;
  });
}
function preventDefault(event) {
  event.preventDefault();
}
function getVisibleElements(root, elements, ratio = 1, axis) {
  if (!(elements == null ? void 0 : elements.length)) return [];
  const rootRect = root.getBoundingClientRect();
  return elements.filter((element) => {
    const { width, height, top, right, left, bottom } = element.getBoundingClientRect();
    if (ratio < 1) {
      const intersectionLeft = Math.max(rootRect.left, left);
      const intersectionRight = Math.min(rootRect.right, right);
      const intersectionWidth = Math.max(0, intersectionRight - intersectionLeft);
      if (axis === "x") {
        return width > 0 && intersectionWidth / width >= ratio;
      }
      const intersectionTop = Math.max(rootRect.top, top);
      const intersectionBottom = Math.min(rootRect.bottom, bottom);
      const intersectionHeight = Math.max(0, intersectionBottom - intersectionTop);
      if (axis === "y") {
        return height > 0 && intersectionHeight / height >= ratio;
      }
      const intersectionArea = intersectionWidth * intersectionHeight;
      const elementArea = width * height;
      return elementArea > 0 && intersectionArea / elementArea >= ratio;
    }
    const isWithinX = left >= rootRect.left && right <= rootRect.right;
    if (axis === "x") {
      return isWithinX;
    }
    const isWithinY = top >= rootRect.top && bottom <= rootRect.bottom;
    if (axis === "y") {
      return isWithinY;
    }
    return isWithinX && isWithinY;
  });
}
function getIOSVersion() {
  var _a;
  const { userAgent } = navigator;
  const isIOS = /(iPhone|iPad)/i.test(userAgent);
  if (!isIOS) return null;
  const version = (_a = userAgent.match(/OS ([\d_]+)/)) == null ? void 0 : _a[1];
  const [major, minor] = (version == null ? void 0 : version.split("_")) || [];
  if (!version || !major) return null;
  return {
    fullString: version.replace("_", "."),
    major: parseInt(major, 10),
    minor: minor ? parseInt(minor, 10) : 0
  };
}
function getCardsToAnimate(grid, cards) {
  var _a, _b, _c, _d;
  if (!grid || !cards || cards.length === 0) return 0;
  const itemSample = cards[0];
  if (!itemSample) return 0;
  const gridRect = grid.getBoundingClientRect();
  const visibleArea = {
    top: Math.max(0, gridRect.top),
    bottom: Math.min(window.innerHeight, gridRect.bottom)
  };
  const visibleHeight = Math.round(visibleArea.bottom - visibleArea.top);
  if (visibleHeight <= 0) return 0;
  const cardSample = itemSample.querySelector("product-card");
  const gridStyle = getComputedStyle(grid);
  const galleryAspectRatio = ((_b = (_a = cardSample == null ? void 0 : cardSample.refs) == null ? void 0 : _a.cardGallery) == null ? void 0 : _b.style.getPropertyValue("--gallery-aspect-ratio")) || "";
  let aspectRatio = parseFloat(galleryAspectRatio) || 0.5;
  if (galleryAspectRatio == null ? void 0 : galleryAspectRatio.includes("/")) {
    const [width = "1", height = "2"] = galleryAspectRatio.split("/");
    aspectRatio = parseInt(width, 10) / parseInt(height, 10);
  }
  const cardGap = parseInt(((_d = (_c = cardSample == null ? void 0 : cardSample.refs) == null ? void 0 : _c.productCardLink) == null ? void 0 : _d.style.getPropertyValue("--product-card-gap")) || "") || 12;
  const gridGap = parseInt(gridStyle.getPropertyValue("--product-grid-gap")) || 12;
  const detailsSize = ((parseInt(gridStyle.fontSize) || 16) + 2) * 2;
  const isMobile = window.innerWidth < 750;
  const cardWidth = isMobile ? Math.round((gridRect.width - gridGap) / 2) : 100;
  const cardHeight = Math.round(cardWidth / aspectRatio) + cardGap + detailsSize;
  const columnsInGrid = isMobile ? 2 : Math.floor((gridRect.width + gridGap) / (cardWidth + gridGap));
  const rowsInGrid = Math.ceil((visibleHeight - gridGap) / (cardHeight + gridGap));
  return columnsInGrid * rowsInGrid;
}
function preloadImage(src) {
  const image = new Image();
  image.src = src;
}
class TextComponent extends HTMLElement {
  shimmer() {
    this.setAttribute("shimmer", "");
  }
}
if (!customElements.get("text-component")) {
  customElements.define("text-component", TextComponent);
}
function resetShimmer(container = document.body) {
  const shimmer = container.querySelectorAll("[shimmer]");
  shimmer.forEach((item) => item.removeAttribute("shimmer"));
}
function changeMetaThemeColor(color) {
  const metaThemeColor = document.head.querySelector('meta[name="theme-color"]');
  if (metaThemeColor && color) {
    metaThemeColor.setAttribute("content", color);
  }
}
function getViewParameterValue() {
  return new URLSearchParams(window.location.search).get("view");
}
function parseIntOrDefault(value, defaultValue) {
  if (value === null || value === void 0 || value === "") {
    return defaultValue;
  }
  const parsed = parseInt(value.toString());
  return isNaN(parsed) ? defaultValue : parsed;
}
class Scheduler {
  constructor() {
    __privateAdd(this, _queue, /* @__PURE__ */ new Set());
    __privateAdd(this, _scheduled, false);
    __publicField(this, "schedule", async (task) => {
      __privateGet(this, _queue).add(task);
      if (!__privateGet(this, _scheduled)) {
        __privateSet(this, _scheduled, true);
        if (viewTransition.current) await viewTransition.current;
        requestAnimationFrame(this.flush);
      }
    });
    __publicField(this, "flush", () => {
      for (const task of __privateGet(this, _queue)) {
        setTimeout(task, 0);
      }
      __privateGet(this, _queue).clear();
      __privateSet(this, _scheduled, false);
    });
  }
}
_queue = /* @__PURE__ */ new WeakMap();
_scheduled = /* @__PURE__ */ new WeakMap();
const scheduler = new Scheduler();
function oncePerEditorSession(element, sessionKeyName, callback) {
  var _a;
  const isInThemeEditor = (_a = window.Shopify) == null ? void 0 : _a.designMode;
  const shopifyEditorSectionId = JSON.parse(element.dataset.shopifyEditorSection || "{}").id;
  const shopifyEditorBlockId = JSON.parse(element.dataset.shopifyEditorBlock || "{}").id;
  const editorId = shopifyEditorSectionId || shopifyEditorBlockId;
  const uniqueSessionKey = "".concat(sessionKeyName, "-").concat(editorId);
  if (isInThemeEditor && sessionStorage.getItem(uniqueSessionKey)) return;
  callback();
  if (isInThemeEditor) sessionStorage.setItem(uniqueSessionKey, "true");
  return;
}
class ResizeNotifier extends ResizeObserver {
  /**
   * @param {ResizeObserverCallback} callback
   */
  constructor(callback) {
    super((entries) => {
      if (__privateGet(this, _initialized)) return callback(entries, this);
      __privateSet(this, _initialized, true);
    });
    __privateAdd(this, _initialized, false);
  }
  disconnect() {
    __privateSet(this, _initialized, false);
    super.disconnect();
  }
}
_initialized = /* @__PURE__ */ new WeakMap();
function setHeaderMenuStyle() {
  const headerComponent = (
    /** @type {HTMLElement} | null */
    document.querySelector("#header-component")
  );
  if (headerComponent) {
    const lockedMenuStyle = headerComponent.dataset.menuStyleLock;
    if (lockedMenuStyle === "drawer" || lockedMenuStyle === "menu") {
      headerComponent.dataset.menuStyle = lockedMenuStyle;
      return;
    }
    window.requestAnimationFrame(() => {
      const overflowList = headerComponent == null ? void 0 : headerComponent.querySelector("overflow-list");
      const hasReachedMinimum = overflowList && overflowList.hasAttribute("minimum-reached");
      headerComponent.dataset.menuStyle = isTouchDevice() || hasReachedMinimum ? "drawer" : "menu";
    });
  }
}
function calculateHeaderGroupHeight(header = document.querySelector("#header-component"), headerGroup = document.querySelector("#header-group")) {
  var _a;
  if (!headerGroup) return 0;
  let totalHeight = 0;
  const children = headerGroup.children;
  for (let i = 0; i < children.length; i++) {
    const element = children[i];
    if (element === header || !(element instanceof HTMLElement)) continue;
    totalHeight += element.offsetHeight;
  }
  if (header instanceof HTMLElement && header.hasAttribute("transparent") && ((_a = header.parentElement) == null ? void 0 : _a.nextElementSibling)) {
    return totalHeight + header.offsetHeight;
  }
  return totalHeight;
}
function updateTransparentHeaderOffset() {
  var _a;
  const header = document.querySelector("#header-component");
  const headerGroup = document.querySelector("#header-group");
  const hasHeaderSection = headerGroup == null ? void 0 : headerGroup.querySelector(".header-section");
  if (!hasHeaderSection || !(header == null ? void 0 : header.hasAttribute("transparent"))) {
    document.body.style.setProperty("--transparent-header-offset-boolean", "0");
    return;
  }
  const hasImmediateSection = (_a = hasHeaderSection.nextElementSibling) == null ? void 0 : _a.classList.contains("shopify-section");
  const shouldApplyOffset = !hasImmediateSection ? "1" : "0";
  document.body.style.setProperty("--transparent-header-offset-boolean", shouldApplyOffset);
}
function updateHeaderHeights() {
  const header = document.querySelector("header-component");
  if (!(header instanceof HTMLElement)) return;
  const headerHeight = header.offsetHeight;
  const headerGroupHeight = calculateHeaderGroupHeight(header);
  const headerTopRow = (
    /** @type {HTMLElement} | null */
    header.querySelector(".header__row--top")
  );
  document.body.style.setProperty("--header-height", "".concat(headerHeight, "px"));
  document.body.style.setProperty("--header-group-height", "".concat(headerGroupHeight, "px"));
  if (headerTopRow) {
    window.requestAnimationFrame(function() {
      header.style.setProperty("--top-row-height", "".concat(headerTopRow.offsetHeight, "px"));
    });
  }
}
function updateAllHeaderCustomProperties() {
  updateHeaderHeights();
  updateTransparentHeaderOffset();
  setHeaderMenuStyle();
}
if (typeof Theme !== "undefined") {
  Theme.utilities = {
    ...Theme.utilities,
    scheduler
  };
}
export {
  ResizeNotifier,
  TextComponent,
  calculateHeaderGroupHeight,
  center,
  changeMetaThemeColor,
  clamp,
  closest,
  debounce,
  fetchCartJson,
  fetchConfig,
  fetchWithDeadline,
  getIOSVersion,
  getViewParameterValue,
  getVisibleElements,
  isClickedOutside,
  isDesktopBreakpoint,
  isLowPowerDevice,
  isMetaInAppBrowser,
  isMobileBreakpoint,
  isPointWithinElement,
  isTouchDevice,
  lockScroll,
  mediaQueryLarge,
  normalizeString,
  onAnimationEnd,
  onDocumentLoaded,
  onDocumentReady,
  oncePerEditorSession,
  parseIntOrDefault,
  prefersReducedMotion,
  preloadImage,
  preventDefault,
  removeWillChangeOnAnimationEnd,
  requestIdleCallback,
  resetShimmer,
  scheduler,
  setCartCheckout,
  setHeaderMenuStyle,
  start,
  startViewTransition,
  supportsViewTransitions,
  throttle,
  unlockScroll,
  updateAllHeaderCustomProperties,
  viewTransition,
  yieldToMainThread
};
